import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'hooks/useAuth';
import { useLicenseStatus } from 'hooks/useLicenseStatus';
import { ROUTES } from 'constants/index';

interface LicenseGateProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  disabled?: boolean;
}
 
export const LicenseGate: React.FC<LicenseGateProps> = ({ 
  children, 
  fallback,
  disabled = false 
}) => {
  const { user } = useAuth();
  const { isExpired, hasLicense } = useLicenseStatus();
  const location = useLocation();
 
  const isCompanyUser = Boolean(user && user.roleId && Number(user.roleId) !== 1);
  const isOnDashboard = location.pathname === ROUTES.DASHBOARD;
  const isOnPricingPage = location.pathname === ROUTES.PRICING;
  const isOnUpgradePaymentPage = location.pathname.startsWith(ROUTES.UPGRADE_PAYMENT);
  const isOnUpgradePackagePage = location.pathname === ROUTES.UPGRADE_PACKAGE;
 
  if (isCompanyUser && (!hasLicense || isExpired) && !isOnDashboard && !isOnPricingPage && !isOnUpgradePaymentPage && !isOnUpgradePackagePage) {
    if (fallback) {
      return <>{fallback}</>;
    }
     
    return (
      <div className="opacity-50 pointer-events-none relative">
        {children}
        <div className="absolute inset-0 bg-gray-100/50 flex items-center justify-center">
          <span className="text-sm text-gray-500 font-medium">
            {!hasLicense ? 'No license - Purchase required' : 'Subscription expired - Renewal required'}
          </span>
        </div>
      </div>
    );
  }
 
  if (disabled) {
    return (
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
    );
  }

  return <>{children}</>;
};
 
export const useLicenseGate = () => {
  const { user } = useAuth();
  const { isExpired, hasLicense } = useLicenseStatus();
  const location = useLocation();

  const isCompanyUser = Boolean(user && user.roleId && Number(user.roleId) !== 1);
  const isOnDashboard = location.pathname === ROUTES.DASHBOARD;
  const isOnPricingPage = location.pathname === ROUTES.PRICING;
  const isOnUpgradePaymentPage = location.pathname.startsWith(ROUTES.UPGRADE_PAYMENT);
  const isOnUpgradePackagePage = location.pathname === ROUTES.UPGRADE_PACKAGE;

  const shouldDisable = isCompanyUser && (hasLicense && isExpired) && !isOnDashboard && !isOnPricingPage && !isOnUpgradePaymentPage && !isOnUpgradePackagePage;

  return {
    shouldDisable,
    isCompanyUser,
    isExpired,
    hasLicense,
    isOnDashboard,
  };
};
